import React, { useEffect, useState, useCallback } from 'react';
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheckCircle,
  FaTimesCircle,
  FaTrash,
} from 'react-icons/fa';
import { FaPencil } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';

function AccountsTable({ setMessage }) {
  const navigate = useNavigate();
  const PAGE_SIZE = 5;
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetch(
        process.env.REACT_APP_FACES_API_BASE + '/accounts',
        {
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('facesIDToken')}`,
          },
        }
      );

      if (!result.ok) {
        console.log('Error fetching accounts: ' + result.statusText);
        setAccounts([]);
        return;
      }

      const data = await result.json();
      const accounts = [];
      data.forEach((account) => {
        accounts.push({
          accountID: account.account_id,
          accountName: account.account_name,
          email: account.admin_email,
          totalSeats: account.total_seats,
          dateCreated: account.date_created,
          expirationDate: account.account_renewal_date,
          stackName: account.stack_name,
        });
      });

      setAccounts(accounts);
      setTotal(accounts.length);
      setLoading(false);
    } catch (error) {
      setMessage('Error fetching accounts: ' + error.message, 'error');
      setAccounts([]);
      setLoading(false);
    }
  }, [setMessage]);

  async function deleteAccount(account_id) {
    if (
      window.confirm(
        'Are you sure you want to delete this account? There is no way to undo this.'
      )
    ) {
      try {
        setLoading(true);
        const result = await fetch(
          process.env.REACT_APP_FACES_API_BASE + '/accounts',
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + localStorage.getItem('facesIDToken'),
            },
            body: JSON.stringify({
              account_id: account_id,
              dry_run: false,
            }),
          }
        );

        if (!result.ok) {
          setMessage('Failed to delete account: ' + result.statusText, 'error');
          return;
        }
        setMessage('Account Deleted.', 'success');
      } catch (error) {
        setMessage('Error deleting account: ' + error.message, 'error');
        setLoading(false);
      }

      setLoading(false);
      fetchAccounts();
    }
  }

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts, page, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const displayRows =
    accounts.length >= pageSize
      ? accounts
      : [...accounts, ...Array(pageSize - accounts.length).fill(null)];

  const handlePageSizeChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setPageSize(newSize);
    setPage(1); // Reset to the first page when page size changes
  };

  return (
    <div style={{ width: '100%', display: 'block', overflowX: 'auto' }}>
      <div
        style={{
          maxHeight: 320,
          overflowY: accounts.length > pageSize ? 'auto' : 'visible',
          borderRadius: 8,
          border: '1px solid #e0e0e0',
          boxShadow: '0 2px 8px rgba(50,56,104,0.06)',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            color: '#23284d',
            background: loading ? '#fff' : undefined,
          }}
        >
          <thead>
            <tr style={{ background: '#f5f6fa' }}>
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'left',
                  fontWeight: 600,
                }}
              >
                ACCOUNT NAME
              </th>
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'left',
                  fontWeight: 600,
                }}
              >
                ACCOUNT ID
              </th>
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'left',
                  fontWeight: 600,
                }}
              >
                ADMIN EMAIL
              </th>
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'center',
                  fontWeight: 600,
                }}
              >
                TOTAL SEATS
              </th>
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'center',
                  fontWeight: 600,
                }}
              >
                DATE CREATED
              </th>
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'center',
                  fontWeight: 600,
                }}
              >
                EXPIRATION DATE
              </th>
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'center',
                  fontWeight: 600,
                }}
              >
                EXPIRED?
              </th>
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'center',
                  fontWeight: 600,
                }}
              >
                EDIT
              </th>
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'center',
                  fontWeight: 600,
                }}
              >
                DELETE
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={8}
                  style={{ textAlign: 'center', padding: '1.2rem' }}
                >
                  Loading accounts...
                </td>
              </tr>
            ) : accounts.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  style={{ textAlign: 'center', padding: '1.2rem' }}
                >
                  No accounts available.
                </td>
              </tr>
            ) : (
              displayRows.map((account, idx) => {
                const isExpired =
                  new Date(account?.expirationDate) < new Date();

                return (
                  <tr
                    key={account ? account.accountID : `empty-${idx}`}
                    style={{
                      background: idx % 2 === 0 ? '#fff' : '#f0f1f6',
                      color: '#23284d',
                      height: 40, // Ensure consistent row height
                      minHeight: 20,
                    }}
                  >
                    <td style={{ padding: '0.4rem', fontFamily: 'monospace' }}>
                      {account ? account.accountName : ''}
                    </td>
                    <td style={{ padding: '0.4rem', fontFamily: 'monospace' }}>
                      {account ? account.accountID : ''}
                    </td>
                    <td style={{ padding: '0.4rem' }}>
                      {account ? account.email : ''}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {account ? account.totalSeats : ''}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {account ? account.dateCreated : ''}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {account ? account.expirationDate : ''}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {account ? (
                        isExpired ? (
                          <FaTimesCircle color="#ba383d" title="Expired" />
                        ) : (
                          <FaCheckCircle color="#27ae60" title="Active" />
                        )
                      ) : (
                        ''
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {account && (
                        <button
                          onClick={() => {
                            navigate(`/menu/faces/editaccount?account_id=${account.accountID}`);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            marginTop: 0,
                          }}
                        >
                          <FaPencil color="#00f" title="Edit account" />
                        </button>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {account && (
                        <button
                          onClick={() => {
                            deleteAccount(account.accountID);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            marginTop: 0,
                          }}
                        >
                          <FaTrash color="#ba383d" title="Delete account" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: '0.5rem',
            gap: 16,
          }}
        >
          <div>
            <label htmlFor="pageSize" style={{ marginRight: '8px' }}>
              Page Size:
            </label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={handlePageSizeChange}
              style={{
                padding: '0.25rem',
                borderRadius: 4,
                border: '1px solid #e0e0e0',
              }}
            >
              {[5, 15, 25, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              className="button"
              style={{ marginTop: 0, padding: '0.45rem 1rem' }}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <FaArrowLeft />
            </button>
            <span style={{ color: '#fff', fontWeight: 600 }}>
              {page}/{totalPages}
            </span>
            <button
              className="button"
              style={{ marginTop: 0, padding: '0.45rem 1rem' }}
              onClick={() => setPage((p) => Math.max(1, p + 1))}
              disabled={page === 1}
            >
              <FaArrowRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountsTable;
